import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  computed,
  contentChildren,
  effect,
  inject,
  model,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { I18nPipe } from "@bitwarden/ui-common";

import { BerryComponent } from "../../berry/berry.component";
import { ButtonModule } from "../../button";
import { ChipComponent } from "../../chips";
import { DialogRef, DialogService } from "../../dialog";
import {
  FilterDialogComponent,
  FilterDialogParams,
} from "../../filter-menu/filter-dialog.component";
import {
  FILTER_PRESENTER,
  FilterPresenter,
  FilterSelection,
} from "../../filter-menu/filter-tokens";
import { IconButtonModule } from "../../icon-button";
import { CollapseOnScrollDirective } from "../../layout/collapse-on-scroll.directive";
import {
  OverflowItemDirective,
  OverflowListDirective,
  OverflowTriggerDirective,
} from "../../overflow-list";
import { TooltipDirective } from "../../tooltip";
import { focusAfterRender } from "../../utils/focus-after-render";
import { isAtOrLargerThanBreakpointSignal } from "../../utils/responsive-utils";

import { BitTableV2Component } from "./table-v2.component";

/**
 * Toolbar for `bit-table-v2`. Project a `<bit-search>` (its own slot), filter chips,
 * and arbitrary controls via `slot="end"`. Chips register with the table directly;
 * the toolbar doesn't own filter state.
 */
@Component({
  selector: "bit-table-toolbar",
  templateUrl: "./bit-table-toolbar.component.html",
  imports: [
    I18nPipe,
    IconButtonModule,
    BerryComponent,
    ChipComponent,
    ButtonModule,
    OverflowListDirective,
    OverflowItemDirective,
    OverflowTriggerDirective,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "tw-block",
    "[class]": "hostClasses()",
  },
})
export class BitTableToolbarComponent {
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the collapsed filter dialog is open. Two-way so a consumer can persist it. */
  readonly filterDialogOpen = model(false);

  private readonly dialogRef = signal<DialogRef<unknown, FilterDialogComponent> | undefined>(
    undefined,
  );

  /** The table this toolbar is projected into; the source of the item count. */
  protected readonly table = inject(BitTableV2Component, { optional: true });

  /** Whether the viewport is wide enough for the inline chip row (vs. the dialog). */
  protected readonly isLargeScreen = isAtOrLargerThanBreakpointSignal("md");

  /** The projected filters, matched by their shared `FILTER_PRESENTER` contract. */
  private readonly filters = contentChildren(FILTER_PRESENTER, { descendants: true });

  /** Whether any filter chips are projected — false for a search-only toolbar. */
  protected readonly hasFilters = computed(() => this.filters().length > 0);

  /** How many projected filters currently have a selection — the trigger's berry count. */
  readonly appliedCount = computed(() => this.filters().filter((f) => f.active()).length);

  /** The filters with a selection — shown as dismissible chips on the small-screen filter row. */
  protected readonly activeFilters = computed(() => this.filters().filter((f) => f.active()));

  /** The projected chips, as overflow items — the toolbar feeds these to the list. */
  private readonly projectedItems = contentChildren(OverflowItemDirective, { descendants: true });

  /** Overflow items declared in this template (the "Clear all" button). */
  private readonly localItems = viewChildren(OverflowItemDirective);

  /**
   * Everything competing for the chip row, in DOM order. Passed to `bitOverflowList`
   * via `items`, since it can't query this component's content itself.
   */
  protected readonly overflowItems = computed(() => [
    ...this.projectedItems(),
    ...this.localItems(),
  ]);

  private readonly overflowList = viewChild(OverflowListDirective);
  private readonly filterRowEl = viewChild<ElementRef<HTMLElement>>("filterRow");
  private readonly injector = inject(Injector);

  /**
   * Whether the filter row is collapsed to the single trigger + dialog. Always below
   * `md`; above it, once the row stops fitting on one line. Gated on `ready()` so the
   * first paint doesn't flash the collapsed view.
   */
  protected readonly collapsed = computed(() => {
    if (!this.isLargeScreen()) {
      return true;
    }
    const list = this.overflowList();
    return (list?.ready() && list.overflow().length > 0) ?? false;
  });

  /** Whether the collapsed trigger renders: the single button that stands in for the chip row. */
  protected readonly showFilterTrigger = computed(() => this.collapsed() && this.hasFilters());

  /** Above `md` the collapse decision isn't known until the chip row has been measured. */
  private readonly collapseSettled = computed(
    () => !this.isLargeScreen() || (this.overflowList()?.ready() ?? false),
  );

  /** Whether a filter row renders below the search row; gates the divider between the two. */
  protected readonly hasFilterRow = computed(() =>
    this.collapsed() ? this.activeFilters().length > 0 : this.hasFilters(),
  );

  /**
   * An enabled `bitCollapseOnScroll` on this element draws the page's seam, so the toolbar leaves
   * the border to it — two `border-color` utilities on one host resolve by stylesheet order. Opted
   * out, the directive draws nothing and the toolbar keeps its own divider.
   */
  private readonly collapse = inject(CollapseOnScrollDirective, { optional: true, self: true });

  protected readonly hostClasses = computed(() => {
    if (this.collapse?.bitCollapseOnScroll()) {
      return "";
    }

    return [
      "tw-border-0",
      "tw-border-b",
      "tw-border-solid",
      "tw-transition-colors",
      "tw-duration-200",
      this.isList() && !this.table?.isScrolled()
        ? "tw-border-transparent"
        : "tw-border-border-base",
    ].join(" ");
  });

  private readonly isList = computed(() => this.table?.presentation() === "list");

  protected readonly insetX = computed(() => (this.isList() ? "tw-px-3" : "tw-px-5"));

  /**
   * The chip row. Collapsed, it stays laid out but invisible so `bitOverflowList` can
   * keep measuring it — `display: none` would zero every width and bounce it back open.
   */
  protected readonly filterRowClasses = computed(() => [
    "tw-flex",
    "tw-flex-wrap",
    "tw-items-center",
    "tw-gap-2",
    this.insetX(),
    ...(this.isList() ? ["tw-pt-0", "tw-pb-2"] : ["tw-py-3.5", "tw-min-h-[60px]"]),
    "empty:tw-hidden",
    ...(this.collapsed()
      ? ["tw-invisible", "tw-pointer-events-none", "tw-absolute", "tw-inset-x-0", "tw-top-0"]
      : []),
  ]);

  protected readonly activeFilterRowClasses = computed(() => [
    "tw-flex",
    "tw-flex-wrap",
    "tw-items-center",
    "tw-gap-2",
    this.insetX(),
    ...(this.isList() ? ["tw-pt-0", "tw-pb-2"] : ["tw-py-3"]),
  ]);

  protected readonly searchRowClasses = computed(() => [
    "tw-flex",
    "tw-flex-wrap",
    "tw-items-center",
    "tw-gap-x-3",
    // Row gap for when the `slot=end` controls wrap to their own line below `md`.
    "tw-gap-y-4",
    ...(this.isList() ? ["tw-py-3"] : ["tw-py-5"]),
    this.insetX(),
    ...(this.hasFilterRow() && !this.isList()
      ? ["tw-border-0", "tw-border-b", "tw-border-solid", "tw-border-border-base"]
      : []),
  ]);

  /** The projected search. Capped on wide viewports; below `md` it fills its row. */
  protected readonly searchClasses = computed(() => [
    "tw-flex",
    "tw-min-w-0",
    "tw-flex-1",
    ...(this.isLargeScreen() ? ["tw-max-w-[25rem]"] : []),
  ]);

  /** The projected `slot=end` controls. Below `md` they wrap to a full-width line. */
  protected readonly endSlotClasses = computed(() => [
    "tw-flex",
    "tw-items-center",
    "tw-gap-3",
    "empty:tw-hidden",
    ...(this.isLargeScreen() ? ["tw-ms-auto"] : ["tw-w-full", "[&>*]:tw-flex-1"]),
  ]);

  constructor() {
    // Chips and the item count change width in place — a chip's label grows to
    // "Type: Login", the count gains a digit (and its width is what the row reserves
    // for the trigger). `bitOverflowList` only remeasures when the item set changes,
    // so its cached widths would go stale and the collapse decision with them.
    effect(() => {
      for (const filter of this.filters()) {
        filter.active();
        filter.summary();
      }
      this.countDigits();
      this.overflowList()?.remeasure();
    });

    // Reconcile the model against the dialog. The trigger gates opening only: a dialog already up
    // stays up when the chip row goes inline, so a resize can't drop a drill-in page or its focus.
    effect(() => {
      const open = this.filterDialogOpen();
      const canOpen = this.showFilterTrigger();
      const settled = this.collapseSettled();
      untracked(() => {
        const ref = this.dialogRef();
        if (open && !ref) {
          if (canOpen) {
            this.showFilterDialog();
          } else if (settled) {
            // No trigger could have opened it, so drop the state rather than let it open later.
            this.filterDialogOpen.set(false);
          }
        } else if (!open && ref) {
          void ref.close();
        }
      });
    });
  }

  /** An active filter's chip label: `label`, or `label: summary` when it has a summary. */
  protected appliedLabel(filter: FilterPresenter): string {
    const summary = filter.summary();
    return summary ? `${filter.label()}: ${summary}` : filter.label();
  }

  /**
   * A per-option chip's `filter: option` label, used as both its tooltip and its accessible
   * name. Built here rather than in the template so no whitespace lands around the colon.
   */
  protected accessibleLabel(filter: FilterPresenter, selection: FilterSelection): string {
    return `${filter.label()}: ${selection.label}`;
  }

  /** Rows matching the active filters — shown as the "N items" count on the filter row. */
  protected readonly itemCount = computed(() => this.table?.filteredCount() ?? 0);

  /** The count's width tracks its digits, not its value — see the remeasure effect. */
  private readonly countDigits = computed(() => String(this.itemCount()).length);

  /** The collapsed trigger's click. */
  protected openFilterDialog(): void {
    this.filterDialogOpen.set(true);
  }

  private showFilterDialog(): void {
    const ref = this.dialogService.open<unknown, FilterDialogParams, FilterDialogComponent>(
      FilterDialogComponent,
      { data: { filters: this.filters } },
    );
    this.dialogRef.set(ref);

    // `takeUntilDestroyed` matters for the persisting consumer: views are destroyed before root
    // providers, so `CdkDialog`'s own teardown can't write a spurious `false` back out.
    ref.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.dialogRef.set(undefined);
      this.filterDialogOpen.set(false);
    });
  }

  /** Whether to offer column customization. */
  protected readonly canCustomizeColumns = computed(
    () => this.table?.canCustomizeColumns() ?? false,
  );

  protected openCustomizeColumns(): void {
    this.table?.openCustomizeColumns();
  }

  /** Reset every projected filter's selection. Excludes search. */
  protected clearAll(): void {
    this.filters().forEach((filter) => filter.clear());
    // Clearing removes this button, so hand focus to the first chip in its row.
    focusAfterRender(this.injector, () =>
      this.filterRowEl()?.nativeElement.querySelector<HTMLElement>("button[bit-chip-content]"),
    );
  }
}
